import {
    ArrowForwardIos,
    CalendarTodayOutlined,
    LightbulbOutlined,
    LocationOnOutlined,
    PersonOutlined,
    ScheduleOutlined,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";

import ImageModal from "../../components/ImageModal";
import { formatDate } from "../../helpers/formatDate";
import { formatTime } from "../../helpers/formatTime";
import { selectAreaById } from "../../stores/areas/areasSelectors";
import { selectInstructorById } from "../../stores/instructors/instructorsSelectors";
import { selectLectureById } from "../../stores/lectures/lecturesSelectors";
import { selectTopicById } from "../../stores/topics/topicsSelectors";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { useState } from "react";

const LectureDetails = () => {
    const { lectureId } = useParams();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const lectureDetails = useSelector(selectLectureById(lectureId || 0));

    // Use safe IDs so selectors don't throw before lecture is loaded
    const topic = useSelector(selectTopicById(lectureDetails?.topic ?? ""));
    const area = useSelector(selectAreaById(lectureDetails?.area ?? ""));
    const hasDate = Boolean(lectureDetails?.date);
    const instructor = useSelector(
        selectInstructorById(lectureDetails?.instructor ?? "")
    );
    const hasTime = Boolean(lectureDetails?.startTime) && Boolean(lectureDetails?.endTime);
    const date = hasDate ? formatDate(lectureDetails?.date || "") : "";
    const startTime = hasTime ? formatTime(lectureDetails?.startTime || "", lectureDetails?.date || "") : "";
    const endTime = hasTime ? formatTime(lectureDetails?.endTime || "", lectureDetails?.date || "") : "";
    const hasVideo = Boolean(lectureDetails?.videoLink);

    try {

    // If the lecture isn't in the store yet (e.g., on refresh), avoid crashing
    if (!lectureDetails) {
        return (
            <PageWrapper>
                <FixedHeader>
                    <Box width="100%" display="flex">
                        <Box
                            onClick={() => navigate("/home")}
                            sx={{
                                width: "30px",
                                height: "30px",
                                borderRadius: "15px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "#4F8CAD",
                                cursor: "pointer",
                            }}
                        >
                            <ArrowForwardIos sx={{ fontSize: 15, color: "#F1EEDB" }} />
                        </Box>
                    </Box>
                </FixedHeader>

                <ScrollableContent>
                    <Typography variant="h6" textAlign="center" sx={{ marginTop: 4 }}>
                        جاري تحميل المحاضرة...
                    </Typography>
                </ScrollableContent>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <ImageModal
                imageSrc={lectureDetails.image || ""}
                open={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
            />

            {/* Fixed Top Section */}
            <FixedHeader>
                <Box width="100%" display="flex">
                    <Box
                        onClick={() => navigate("/home")}
                        sx={{
                            width: "30px",
                            height: "30px",
                            borderRadius: "15px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "#4F8CAD",
                            cursor: "pointer",
                        }}
                    >
                        <ArrowForwardIos
                            sx={{ fontSize: 15, color: "#F1EEDB" }}
                        />
                    </Box>
                </Box>

                {/* Image */}
                <img
                    onClick={() => setIsModalOpen(true)}
                    src={lectureDetails.image || ""}
                    alt={lectureDetails.title}
                    style={{
                        borderRadius: "20px",
                        width: "auto",
                        height: "40vh",
                    }}
                />
            </FixedHeader>

            {/* Scrollable Content */}
            <ScrollableContent>
                {/* Lecture Title */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    gap={2}
                    alignItems="center"
                >
                    <Typography variant="h5">{lectureDetails.title}</Typography>
                </Box>

                <Box display="flex" gap={2} flexDirection="column" width="100%">
                    <Box display="flex" justifyContent="space-between" gap={1}>
                        <StyledPill>
                            <LightbulbOutlined sx={{ color: "#F1EEDB" }} />
                            <PillText variant="h6">{topic?.name || ""}</PillText>
                        </StyledPill>

                        <StyledPill>
                            <PersonOutlined sx={{ color: "#F1EEDB" }} />
                            <PillText variant="h6">
                                {instructor?.fullName || ""}
                            </PillText>
                        </StyledPill>
                    </Box>

                    {(hasDate || hasTime) && (
                        <Box display="flex" justifyContent="space-between" gap={1}>
                            {hasDate && (
                                <StyledPill>
                                    <CalendarTodayOutlined sx={{ color: "#F1EEDB" }} />
                                    <PillText variant="body1">{date}</PillText>
                                </StyledPill>
                            )}

                            {hasTime && (
                                <StyledPill>
                                    <ScheduleOutlined sx={{ color: "#F1EEDB" }} />
                                    <PillText variant="body2">
                                        {startTime}-{endTime}
                                    </PillText>
                                </StyledPill>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Lecture Description */}
                <Box display="flex" flexDirection="column" width="100%">
                    <Typography variant="body1">
                        {lectureDetails.description}
                    </Typography>
                </Box>

                {/* Address */}
                {!hasVideo && (
                    <Box display="flex" flexDirection="column" width="100%">
                        <Typography variant="h6">العنوان</Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            <LocationOnOutlined
                                fontSize="large"
                                sx={{ color: "#4F8CAD" }}
                            />
                            <Box>
                                <Typography>
                                    {area?.name || ""}{" "}
                                    {lectureDetails.addressLine1 &&
                                        ` - ${lectureDetails.addressLine1}`}
                                </Typography>
                                <Typography>
                                    {lectureDetails.addressLine2 &&
                                        `${lectureDetails.addressLine2}`}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Google Maps */}
                {!hasVideo && lectureDetails.googleMaps && (
                    <ExternalLink to={lectureDetails.googleMaps}>
                        <img
                            src="/assets/google-maps.png"
                            alt="google maps"
                            height="25px"
                        />
                        <Typography variant="h6">الموقع</Typography>
                    </ExternalLink>
                )}

                {/* Zoom link */}
                {lectureDetails.zoomLink && (
                    <ExternalLink to={lectureDetails.zoomLink}>
                        <Typography variant="h6">رابط زوم</Typography>
                    </ExternalLink>
                )}

                {lectureDetails.videoLink && (
                    <Box >
                        <ExternalLink to={lectureDetails.videoLink}>
                            <Typography variant="h6">رابط الفيديو</Typography>
                        </ExternalLink>
                    </Box>
                )}
            </ScrollableContent>
        </PageWrapper>
    );
    } catch (error) {
        return (
            <PageWrapper>
                <FixedHeader>
                    <Box width="100%" display="flex">
                        <Box
                            onClick={() => navigate("/home")}
                            sx={{
                                width: "30px",
                                height: "30px",
                                borderRadius: "15px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "#4F8CAD",
                                cursor: "pointer",
                            }}
                        >
                            <ArrowForwardIos sx={{ fontSize: 15, color: "#F1EEDB" }} />
                        </Box>
                    </Box>
                </FixedHeader>

                <ScrollableContent>
                    <Typography variant="h6" textAlign="center" sx={{ marginTop: 4 }}>
                        حدث خطأ في تحميل المحاضرة
                    </Typography>
                </ScrollableContent>
            </PageWrapper>
        );
    }
};

export default LectureDetails;

const PageWrapper = styled(Box)({
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundImage: "url(/assets/tawhadi_background.png)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "repeat",
    paddingTop: 25,
});

const FixedHeader = styled(Box)({
    position: "sticky",
    top: 0,
    zIndex: 10,
    padding: 15,
    display: "flex",
    flexDirection: "column",
    gap: 15,
});

const ScrollableContent = styled(Box)({
    flexGrow: 1,
    overflowY: "auto",
    padding: 15,
    height: "calc(80vh - 250px)",
    display: "flex",
    flexDirection: "column",
    gap: 15,
});

const StyledPill = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: 5,
    backgroundColor: "#4F8CAD",
    padding: "5px 10px",
    width: "100%",
    minHeight: "40px",
    borderRadius: 50,
    color: "#F1EEDB",
});

const PillText = styled(Typography)({
    color: "#F1EEDB",
});

const ExternalLink = styled(Link)({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 50,
    padding: "10px",
    backgroundColor: "black",
    textDecoration: "none",
    color: "white",
    fontWeight: "bold",
    gap: 10,
});
